import { z } from "zod";
import { OPERATOR_MODEL_TIERS } from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Coerce empty / whitespace-only strings to undefined so `.optional()` accepts
 * them. Mirrors the backend `remoteDesktopCredentialsConfigSchema`
 * (backend/src/core/ai/tools/remote-desktop/config.ts) — kept in sync by hand
 * because `packages/*` cannot import from `backend`.
 */
const optionalTrimmedUuid = z.preprocess((value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}, z.string().uuid().optional());

const remoteDesktopStepCredentialsSchema = z.object({
  avdUrl: z
    .string()
    .url()
    .default("https://windows.cloud.microsoft.com/#/devices"),
  // Inline email/password/vmPassword are retained for back-compat only — the
  // operator now logs in by reference (credentialId / vmCredentialId), so these
  // no longer drive login.
  email: z.string().trim().default(""),
  password: z.string().default(""),
  otpTargetId: z.string().optional(),
  vmPassword: z.string().default(""),
  desktopName: z.string().min(1).default("AVD-FullDesktop"),
  authStateName: z.string().optional(),
  headless: z.boolean().default(false),
  desktopLoadWaitSeconds: z.number().int().positive().max(120).default(10),
  credentialId: optionalTrimmedUuid,
  vmCredentialId: optionalTrimmedUuid,
});

export const loginToRemoteDesktopStep = defineStepConfig({
  type: "login_to_remote_desktop",
  name: "Login to Remote Desktop",
  description:
    "Create a remote desktop session and log into it via the computer-use operator. The operator logs in BY REFERENCE using the credential ids configured on this step (credentialId for the portal, vmCredentialId, otpTargetId) — inline email/password are retained for back-compat but no longer drive login. Returns a session ID for downstream remote-desktop steps.",
  category: "Computer Use",

  inputSchema: z.object({}),

  outputSchema: z.object({
    sessionId: z.string().nullable(),
    message: z.string().nullable(),
    error: z.string().nullable(),
    screenshotDocumentId: z.string().nullable(),
    screenshotStoragePath: z.string().nullable(),
  }),

  configSchema: z.object({
    credentials: remoteDesktopStepCredentialsSchema.optional(),
    /**
     * Computer-use model tier that performs the sign-in (and heals a drifted
     * step when `loginSopId` is set). Previously this step had no model
     * setting at all and the sign-in was hardcoded, so the tier shown on the
     * neighboring Desktop Computer Use node was the only one a reader could
     * see — and it governed a different step.
     */
    model: z.enum(OPERATOR_MODEL_TIERS).default("sonnet"),
    /**
     * Log in by replaying this procedure instead of the built-in hardcoded portal sequence.
     *
     * The built-in sequence pins the Microsoft portal's selectors as fixed strings, so a portal
     * redesign fails a stage outright. A login procedure replays the same stages with the
     * replayer's self-heal, and a step that drifts is re-derived and patched back. The credential,
     * OTP, and desktop settings above still apply — they override whatever the procedure was
     * installed with, so one procedure serves nodes pointing at different desktops.
     *
     * Omitted → the built-in sequence, unchanged.
     */
    loginSopId: optionalTrimmedUuid,
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "10 minutes",
    retry: {
      maximumAttempts: 1,
    },
  },
});
